﻿import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import { createSignal, For, Show } from "solid-js";
import {
  IconBuilding2,
  IconCheck,
  IconContact,
  IconLoaderCircle,
  IconMapPin,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from "~/components/icons";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { Separator } from "~/components/ui/separator";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { getBusinessPageData } from "~/features/business/business-page";
import {
  addBusinessClass,
  deleteBusiness,
  deleteBusinessClass,
} from "~/features/business/business.api";
import { useBusinessForm } from "~/features/business/business.form";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import type { BusinessFull } from "~/features/business/business.server";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute("/_dashboard/dashboard/business")({
  staticData: {
    breadcrumb: "Business",
  },
  ...withBusinessGuard("/dashboard"),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );

    if (!collection.activeBusinessId) {
      return { collection, activeBusiness: null };
    }

    const data = await getBusinessPageData({
      data: { businessId: collection.activeBusinessId },
    });
    businessDetailResource.seed(
      context.queryClient,
      collection.activeBusinessId,
      data.businessDetail,
    );

    return { collection, activeBusiness: data.businessDetail };
  },
  component: BusinessPage,
});

type Mode = "view" | "edit";

// -- Page ----------------------------------------------------------------------

function BusinessPage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();

  const activeBusiness = data().activeBusiness;
  if (activeBusiness?.id) {
    businessDetailResource.seedIfEmpty(qc, activeBusiness.id, activeBusiness);
  }

  const activeId = useActiveBusinessId();

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(activeId() ?? "", !!activeId()),
    placeholderData: keepPreviousData,
  }));

  const [mode, setMode] = createSignal<Mode>("view");

  return (
    <div class="flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 @5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto">
      <Show when={businessQuery.isError}>
        <div class="flex flex-col items-center justify-center py-24 text-destructive">
          <p class="text-sm">Failed to load business. Please refresh.</p>
        </div>
      </Show>
      <Show
        when={!businessQuery.isLoading && !businessQuery.isError}
        fallback={
          <div role="status" class="w-full animate-pulse">
            <div class="h-150 bg-muted rounded-lg w-full" />
          </div>
        }
      >
        <Show
          when={businessQuery.data}
          fallback={
            <div class="flex flex-col items-center justify-center py-24 text-muted-foreground">
              <IconBuilding2 class="size-12 mb-4 opacity-40" />
              <p class="text-lg font-medium">No business selected</p>
              <p class="text-sm mt-1">
                Select or create a business to get started.
              </p>
            </div>
          }
        >
          {(biz) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <BusinessView
                  business={biz()}
                  setMode={setMode}
                  onDeleted={() => {
                    // Synchronously evict the deleted business from the cache and
                    // clear the active business id BEFORE navigating. Otherwise the
                    // /dashboard loader's ensureQueryData can read the stale cached
                    // collection (still pointing at the deleted business) and fire
                    // getDashboardPageData against it, producing 404s from
                    // assertBusinessOwner for every dashboard sub-query.
                    qc.setQueryData(
                      businessesResource.options(undefined).queryKey,
                      (current) => {
                        if (!current) return current;
                        return {
                          ...current,
                          businesses: current.businesses.filter(
                            (b) => b.id !== biz().id,
                          ),
                          activeBusinessId:
                            current.activeBusinessId === biz().id
                              ? null
                              : current.activeBusinessId,
                        };
                      },
                    );
                    businessesResource.invalidate(qc, undefined);
                    businessDetailResource.remove(qc, biz().id);
                  }}
                />
              }
            >
              <BusinessEdit
                business={biz()}
                setMode={setMode}
                onSaved={() => businessDetailResource.invalidate(qc, biz().id)}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteBusinessDialog(props: {
  business: BusinessFull;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = createSignal("");
  const [isDeleting, setIsDeleting] = createSignal(false);
  const expectedText = () => `Delete ${props.business.companyName}`;
  const isValid = () =>
    confirmText().toLowerCase() === expectedText().toLowerCase();

  async function handleDelete() {
    if (!isValid()) return;
    setIsDeleting(true);
    try {
      await deleteBusiness({ data: { businessId: props.business.id } });
      props.onDeleted();
      navigate({ to: "/dashboard" });
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete{" "}
            <span class="font-semibold">"{props.business.companyName}"</span>{" "}
            and all associated data.
          </DialogDescription>
        </DialogHeader>
        <div class="space-y-4">
          <div class="space-y-2">
            <p class="text-sm text-muted-foreground">
              To confirm, type{" "}
              <span class="font-semibold">{expectedText()}</span> below:
            </p>
            <TextField>
              <TextFieldInput
                type="text"
                placeholder={expectedText()}
                value={confirmText()}
                onInput={(e) => setConfirmText(e.currentTarget.value)}
              />
            </TextField>
          </div>
        </div>
        <DialogFooter class="flex-col gap-2 sm:flex-row">
          <Button
            variant="destructive"
            disabled={!isValid() || isDeleting()}
            onClick={handleDelete}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Business"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View ----------------------------------------------------------------------

function BusinessView(props: {
  business: BusinessFull;
  setMode: (m: Mode) => void;
  onDeleted: () => void;
}) {
  const biz = () => props.business;
  const qc = useQueryClient();

  const [newClass, setNewClass] = createSignal("");
  const [addingClass, setAddingClass] = createSignal(false);
  const [deletingClassId, setDeletingClassId] = createSignal<string | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = createSignal(false);

  async function handleAddClass() {
    const name = newClass().trim();
    if (!name) return;
    setAddingClass(true);
    try {
      await addBusinessClass({ data: { businessId: biz().id, name } });
      setNewClass("");
      businessDetailResource.invalidate(qc, biz().id);
    } finally {
      setAddingClass(false);
    }
  }

  async function handleDeleteClass(classId: string) {
    setDeletingClassId(classId);
    try {
      await deleteBusinessClass({ data: { classId, businessId: biz().id } });
      businessDetailResource.invalidate(qc, biz().id);
    } finally {
      setDeletingClassId(null);
    }
  }

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconBuilding2 class="size-5" />}
        title={biz().companyName}
        badge={biz().industry ?? undefined}
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <StandardRouteDetailCard
              icon={<IconContact class="size-5" />}
              title="Company"
              contentClass="divide-y divide-border"
              action={
                <Tooltip>
                  <TooltipTrigger
                    as={Button<"button">}
                    variant="ghost"
                    size="icon"
                    class="size-7 text-muted-foreground hover:text-foreground"
                    aria-label="Edit business"
                    onClick={() => props.setMode("edit")}
                  >
                    <IconPencil class="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              }
            >
              <InfoRow label="Name" value={biz().companyName} />
              <InfoRow label="Legal Name" value={biz().legalName} />
              <InfoRow label="Company Type" value={biz().companyType} />
              <InfoRow label="Industry" value={biz().industry} />
              <InfoRow label="TIN / EIN" value={biz().tin} />
            </StandardRouteDetailCard>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="contact"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="contact" class="flex-1">
                      <IconContact class="mr-2 size-4" />
                      Contact
                    </TabsTrigger>
                    <TabsTrigger value="classes" class="flex-1">
                      <IconCheck class="mr-2 size-4" />
                      Classes
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent value="contact" forceMount>
                      <CardContent class="px-0 pb-6 space-y-6">
                        <div>
                          <div class="flex items-center gap-2 mb-3">
                            <IconContact class="size-4 text-muted-foreground" />
                            <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Contact
                            </p>
                          </div>
                          <div class="divide-y divide-border">
                            <InfoRow label="Email" value={biz().email} />
                            <InfoRow label="Phone" value={biz().phone} />
                            <InfoRow label="Website" value={biz().website} />
                            <InfoRow label="Fax" value={biz().fax} />
                          </div>
                        </div>
                        <div>
                          <div class="flex items-center gap-2 mb-3">
                            <IconMapPin class="size-4 text-muted-foreground" />
                            <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Address
                            </p>
                          </div>
                          <div class="divide-y divide-border">
                            <InfoRow
                              label="Line 1"
                              value={biz().address?.line1}
                            />
                            <InfoRow
                              label="Line 2"
                              value={biz().address?.line2}
                            />
                            <InfoRow label="City" value={biz().address?.city} />
                            <InfoRow
                              label="State"
                              value={biz().address?.state}
                            />
                            <InfoRow
                              label="Postal Code"
                              value={biz().address?.postalCode}
                            />
                            <InfoRow
                              label="Country"
                              value={biz().address?.country}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent value="classes" forceMount>
                      <CardContent class="px-0 space-y-3">
                        <div class="flex flex-wrap gap-1.5">
                          <Show
                            when={biz().classes.length > 0}
                            fallback={
                              <p class="text-sm text-muted-foreground">
                                No classes yet.
                              </p>
                            }
                          >
                            <For each={biz().classes}>
                              {(cls) => (
                                <Badge variant="secondary" class="gap-1.5 pr-1">
                                  {cls.name}
                                  <button
                                    type="button"
                                    class="rounded-full opacity-60 hover:opacity-100 transition-opacity"
                                    disabled={deletingClassId() === cls.id}
                                    onClick={() => handleDeleteClass(cls.id)}
                                  >
                                    <IconX class="size-3" />
                                  </button>
                                </Badge>
                              )}
                            </For>
                          </Show>
                        </div>
                        <div class="flex gap-2 items-center">
                          <TextField class="flex-1">
                            <TextFieldInput
                              type="text"
                              placeholder="New class name"
                              value={newClass()}
                              onInput={(e) =>
                                setNewClass(e.currentTarget.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddClass();
                                }
                              }}
                            />
                          </TextField>
                          <Button
                            size="icon"
                            variant="outline"
                            class="size-7 shrink-0"
                            disabled={!newClass().trim() || addingClass()}
                            onClick={handleAddClass}
                          >
                            <Show
                              when={addingClass()}
                              fallback={<IconPlus class="size-3" />}
                            >
                              <IconLoaderCircle class="size-3 animate-spin" />
                            </Show>
                          </Button>
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteBusinessDialog
        business={biz()}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit ----------------------------------------------------------------------

function BusinessEdit(props: {
  business: BusinessFull;
  setMode: (m: Mode) => void;
  onSaved: () => void;
}) {
  const biz = () => props.business;
  const [serverError, setServerError] = createSignal("");

  const form = useBusinessForm(biz(), {
    onSuccess: () => {
      props.onSaved();
      props.setMode("view");
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  return (
    <StandardRouteDetailEditCard
      title="Edit Business"
      description="Update your business information. Changes apply immediately on save."
      formId="business-edit-form"
      onCancel={() => props.setMode("view")}
      saveButton={
        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              form="business-edit-form"
              disabled={isSubmitting()}
            >
              {isSubmitting() ? "Saving..." : "Update Business"}
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <form
        id="business-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <section class="space-y-4 mb-6">
          <h3 class="text-lg font-semibold">Company Information</h3>
          <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
            <Col>
              <form.Field name="companyName">
                {(field) => (
                  <FormField label="Company Name" required field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Company Name"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="legalName">
                {(field) => (
                  <FormField label="Legal Name" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Legal Name"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="tin">
                {(field) => (
                  <FormField label="TIN / EIN" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="XX-XXXXXXX"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="companyType">
                {(field) => (
                  <FormField label="Company Type" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="LLC, Corporation..."
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col span={1} spanMd={2}>
              <form.Field name="industry">
                {(field) => (
                  <FormField label="Industry" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Industry"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
          </Grid>
        </section>

        <Separator />

        <section class="space-y-4 mb-6 mt-4">
          <h3 class="text-lg font-semibold">Address</h3>
          <Grid cols={1} colsMd={2} class="gap-4">
            <Col span={1} spanMd={2}>
              <form.Field name="address.line1">
                {(field) => (
                  <FormField label="Address Line 1" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Street address"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col span={1} spanMd={2}>
              <form.Field name="address.line2">
                {(field) => (
                  <FormField label="Address Line 2" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Apt, suite, unit..."
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="address.city">
                {(field) => (
                  <FormField label="City" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="City"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="address.state">
                {(field) => (
                  <FormField label="State" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="State"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="address.postalCode">
                {(field) => (
                  <FormField label="Postal Code" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Postal Code"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="address.country">
                {(field) => (
                  <FormField label="Country" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="Country"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
          </Grid>
        </section>

        <Separator />

        <section class="space-y-4 mb-6 mt-4">
          <h3 class="text-lg font-semibold">Contact Information</h3>
          <Grid cols={1} colsMd={2} class="gap-4">
            <Col>
              <form.Field name="email">
                {(field) => (
                  <FormField label="Email" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="email@example.com"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="website">
                {(field) => (
                  <FormField label="Website" field={field()}>
                    <TextFieldInput
                      type="text"
                      placeholder="https://example.com"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="phone">
                {(field) => (
                  <FormField label="Phone" field={field()}>
                    <TextFieldInput
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="fax">
                {(field) => (
                  <FormField label="Fax" field={field()}>
                    <TextFieldInput
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={field().state.value}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      onBlur={field().handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </Col>
          </Grid>
        </section>

        <Show when={serverError()}>
          <p class="text-sm text-destructive mb-4">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

// -- Helpers -------------------------------------------------------------------

function InfoRow(props: { label: string; value: string | null | undefined }) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "N/A"}</h6>
    </div>
  );
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}
